const PROFILE_KEY = 'ingo_user_profile';
/** The old system's copy of the email, no longer written: still cleared from phones that hold it. */
const SESSION_EMAIL_KEY = 'ingo_session_email';

function clearCustomerKeysFrom(storage) {
  try {
    storage.removeItem('ingo_signed_in');
    storage.removeItem(PROFILE_KEY);
    storage.removeItem(SESSION_EMAIL_KEY);
  } catch {
    // ignore
  }
}

/** Where the active customer session lives (session vs persistent). */
function getActiveCustomerStorage() {
  try {
    if (sessionStorage.getItem('ingo_signed_in') === '1') return sessionStorage;
    if (localStorage.getItem('ingo_signed_in') === '1') return localStorage;
    return null;
  } catch {
    return null;
  }
}

/**
 * Persist the signed-in client's profile (the fields the screens read).
 * @param {object} profile - User row fields to cache.
 * @param {{ rememberMe?: boolean }} [options] - If true, persist until logout (localStorage).
 *   If false, only this browser tab session (sessionStorage). If omitted, update whatever store is already active, else localStorage.
 */
export function saveCustomerSession(profile, options = {}) {
  const { rememberMe } = options;
  let targetStorage;
  if (rememberMe === true) {
    targetStorage = localStorage;
    clearCustomerKeysFrom(sessionStorage);
  } else if (rememberMe === false) {
    targetStorage = sessionStorage;
    clearCustomerKeysFrom(localStorage);
  } else {
    targetStorage = getActiveCustomerStorage() || localStorage;
    const other = targetStorage === localStorage ? sessionStorage : localStorage;
    clearCustomerKeysFrom(other);
  }

  try {
    targetStorage.setItem('ingo_signed_in', '1');
    if (profile && typeof profile === 'object') {
      targetStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    }
  } catch {
    // ignore
  }
}

/**
 * A client signed in on InGo's API (M3): the profile the screens read, made
 * from the account the API answers. `source: 'api'` tells a screen the client
 * is InGo's, so it books through the API; the token is in the secure store.
 *
 * @param {{ id: number|string, first_name: string, last_name?: string|null, email?: string|null, phone?: string|null }} account
 * @param {{ rememberMe?: boolean }} [options]
 */
export function startCustomerSession(account, options = {}) {
  saveCustomerSession(
    {
      id: String(account.id),
      full_name: [account.first_name, account.last_name].filter(Boolean).join(' '),
      phone: account.phone ?? '',
      email: account.email ?? '',
      source: 'api',
    },
    options,
  );
  // The phone for their delivery updates, as a rider's is registered at sign-in.
  // Dynamic: clientPush reads this module.
  void import('./clientPush').then((m) => m.ensureClientPushRegistered()).catch(() => {});
}

/** Whether the signed-in client is InGo's (their bookings go through the API). */
export function isApiCustomer() {
  return getCustomerSession()?.source === 'api';
}

export function getCustomerSession() {
  try {
    for (const storage of [sessionStorage, localStorage]) {
      const raw = storage.getItem(PROFILE_KEY);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') continue;
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

export function clearCustomerSession() {
  clearCustomerKeysFrom(localStorage);
  clearCustomerKeysFrom(sessionStorage);
}

export function isCustomerMarkedSignedIn() {
  try {
    return (
      sessionStorage.getItem('ingo_signed_in') === '1' || localStorage.getItem('ingo_signed_in') === '1'
    );
  } catch {
    return false;
  }
}
